import { useState, useEffect } from "react";
import Lottie from "lottie-react";
import yetiWavingJson from "../../../../appAssets/jsons/yetiWaving.json";

interface YetiCharacterProps {
  characterImage?: string | null;
  className?: string;
}

/**
 * Renders the Yeti correctly for both cases:
 *  - characterImage is a Lottie JSON URL  → fetch + render with lottie-react
 *  - characterImage is a PNG/image URL    → render with <img>
 *  - characterImage is null/missing       → fall back to local yetiWaving.json
 *
 * Mirrors mobile logic:
 *   isOnboardingDone  → images.yetiWaving  (local Lottie, same for all rooms)
 *   else              → { uri: characterImage }  (LottieView with URL)
 */
function YetiCharacter({ characterImage, className = "" }: YetiCharacterProps) {
  const [lottieData, setLottieData] = useState<object | null>(null);
  const [fetchFailed, setFetchFailed] = useState(false);

  const isLottieUrl = Boolean(
    characterImage &&
    (characterImage.toLowerCase().includes(".json") ||
      characterImage.toLowerCase().includes("lottie")),
  );

  useEffect(() => {
    if (!characterImage || !isLottieUrl) {
      setLottieData(null);
      setFetchFailed(false);
      return;
    }

    let cancelled = false;
    setLottieData(null);
    setFetchFailed(false);

    fetch(characterImage)
      .then((r) => {
        if (!r.ok) throw new Error("non-ok");
        return r.json();
      })
      .then((json) => {
        if (!cancelled) setLottieData(json);
      })
      .catch(() => {
        if (!cancelled) setFetchFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [characterImage, isLottieUrl]);

  // Lottie URL — use fetched data or fall back to local waving
  if (isLottieUrl) {
    return (
      <Lottie
        animationData={lottieData ?? yetiWavingJson}
        loop
        autoplay
        className={className}
      />
    );
  }

  // PNG/image URL — render as img, fall back to Lottie on error
  if (characterImage && !fetchFailed) {
    return (
      <img
        src={characterImage}
        alt="Yeti"
        className={className}
        onError={() => setFetchFailed(true)}
      />
    );
  }

  // Default — local yetiWaving Lottie
  return (
    <Lottie
      animationData={yetiWavingJson}
      loop
      autoplay
      className={className}
    />
  );
}

export default YetiCharacter;
